<template>
  <div class="expand-phase">
    <Alert
      v-if="incompletePhases && isEditMode"
      class="expand-phase__banner"
      :text="missingFieldsText"
      :vuetifyProps="{
        color: 'yellow',
        icon: 'mdi-information',
      }"
    />
    <div class="expand-phase__content">
      <PhaseSection
        :title="$t('common.rev_state.act')"
        :description="$t('rev_cycle.expand.description')"
        :isEditMode="isEditMode"
        :hasNextButton="hasNextButton"
        :hasBackButton="hasBackButton"
        @next="$emit('next')"
        @back="$emit('back')"
      >
        <MediaContainer
          data-private
          id="expandPhoto"
          slot="left"
          class="mt-6"
          :images="media"
          :readOnly="!isEditMode || incompletePhases || isSaving"
          @change="handleMediaChange"
        />
        <TranslationLoading slot="right" v-if="isTranslating" />
        <div slot="right" v-else>
          <TextQuestion
            v-model="solution"
            data-private
            rows="4"
            tid="improvementIdea-textarea-solution"
            tourId="expand-textField-solution"
            :error="markRequiredFields && didExperimentWork && !solution"
            :maxLength="$constants.improvementIdea.solutionMaxLength"
            :isEditMode="isEditMode"
            :question="
              `${$t('rev_cycle.expand.what_is_the_solution')}${
                didExperimentWork ? '*' : ''
              }`
            "
            :placeholder="
              $t('rev_cycle.expand.what_is_the_solution_textarea_placeholder')
            "
            @focusOut="trackEvent('solution')"
          />

          <TextQuestion
            v-model="whyNotImplemented"
            data-private
            rows="4"
            tid="improvementIdea-textarea-notImplementedReason"
            :class="{ 'mt-0': isEditMode, 'mt-6': !isEditMode }"
            :maxLength="$constants.improvementIdea.solutionMaxLength"
            :question="$t('rev_cycle.expand.why_not_implemented')"
            :placeholder="
              $t('rev_cycle.expand.why_not_implemented_placeholder')
            "
            :isEditMode="isEditMode"
          />
          <div class="w-100 justify-end d-flex">
            <Button
              v-if="!isRevCompleted && didExperimentWork !== false"
              color="primary"
              class="d-flex align-self-end align-center mt-2"
              data-testid="after_complete_rev"
              data-tourid="improvementIdea-button-completeRev"
              :disabled="disableCompleteButton"
              :class="{ 'mt-6': !isEditMode }"
              :text="$t('rev.complete_rev')"
              @click="handleCompleteRev"
            />
          </div>
        </div>
      </PhaseSection>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import Alert from '@/components/Alert'
import PhaseSection from '@/components/Rev/PhaseSection/PhaseSection.vue'
import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import TextQuestion from '@/components/Rev/TextQuestion.vue'
import Button from '@/components/UI/Button'
import TranslationLoading from '@/components/Rev/TranslationLoading.vue'

export default {
  name: 'Expand',

  mixins: [trackerMixin],

  components: {
    Alert,
    PhaseSection,
    MediaContainer,
    TextQuestion,
    TranslationLoading,
    Button,
  },

  props: {
    isEditMode: {
      type: Boolean,
      required: true,
    },
    incompletePhases: {
      type: Boolean,
      required: true,
    },
    rev: {
      type: Object,
      required: true,
    },
    isSaving: Boolean,
    isTranslating: Boolean,
    hasNextButton: Boolean,
    hasBackButton: Boolean,
    canComplete: Boolean,
    markRequiredFields: Boolean,
  },

  computed: {
    disableCompleteButton() {
      return !this.canComplete
    },

    didExperimentWork() {
      return this.rev?.verify?.didExperimentWork
    },

    isVerifyComplete() {
      const { didExperimentWork, learned } = this.rev.verify

      if (didExperimentWork) return true
      return !!learned
    },

    media() {
      return this.rev?.expand?.media ?? []
    },

    missingFieldsText() {
      return this.$t('rev.missing_fields_alert')
    },

    solution: {
      get() {
        return this.rev?.expand?.solution ?? ''
      },
      set(solution) {
        this.$emit('revChanged')
        this.updateSolution(solution)
      },
    },
    whyNotImplemented: {
      get() {
        return this.rev?.expand?.whyNotImplemented ?? ''
      },
      set(whyNotImplemented) {
        this.$emit('revChanged')
        this.updateWhyNotImplemented(whyNotImplemented)
      },
    },
    isRevCompleted() {
      return this.rev.status === this.$constants.revStatus.completed
    },
  },

  methods: {
    ...mapMutations({
      removeExpandPhoto: 'improvementIdea/removeExpandPhoto',
      updateSolution: 'improvementIdea/updateSolution',
      updateIsImplemented: 'improvementIdea/updateIsImplemented',
      updateWhyNotImplemented: 'improvementIdea/updateWhyNotImplemented',
      setExpandMedia: 'improvementIdea/setExpandMedia',
    }),

    trackEvent(changedField) {
      this.trackUpdate(this.rev, changedField)
    },

    handleCompleteRev() {
      this.trackEvent('completeRev')
      this.$emit('completeRev')
    },

    handleMediaChange(media) {
      this.setExpandMedia(media)
      this.trackEvent('expandPhotos')
      this.$emit('revChanged')
    },
  },
}
</script>

<style lang="scss" scoped>
.expand-phase {
  &__banner {
    margin-bottom: 24px;
  }

  &__content {
    display: flex;
  }
}
</style>
